import { CarouselCtaType, WelcomePageConfig } from '@bciti/types';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';

import { Subject } from 'rxjs';

@Component({
    selector: 'app-cta-form',
    templateUrl: './cta-form.component.html',
    styleUrls: ['./cta-form.component.scss'],
})
export class CtaFormComponent implements OnInit {
    private readonly MAX_CTA_SLIDES = 4; //change this to increase max avaible slides for cta

    destroy$ = new Subject();

    @Input()
    welcomePage: WelcomePageConfig | undefined;

    @Input()
    loading: boolean = true;

    @Input()
    displayActions: boolean = true;

    @Output()
    submitForm = new EventEmitter<Partial<WelcomePageConfig>>();

    carousel: CarouselCtaType[] = Object.values(CarouselCtaType);

    welcomePageForm = this.fb.group(
        {
            cta: this.fb.group({
                interval: this.fb.control(2, [Validators.min(2), Validators.max(9), Validators.required]),
                slides: this.fb.array([]),
            }),
        },
        { updateOn: 'change' },
    );

    constructor(private fb: FormBuilder) {
        this.maxSlides.forEach((i) => {
            this.slidesControl.push(
                this.fb.group({
                    activated: this.fb.control(false),
                    type: this.fb.control({ value: null, disabled: true }),
                }),
            );
        });
    }

    ngOnInit(): void {}

    get intervalControl() {
        return this.welcomePageForm.get('cta.interval');
    }

    get maxSlides(): string[] {
        return Array(this.MAX_CTA_SLIDES).fill('');
    }

    get slidesControl() {
        return this.welcomePageForm.get('cta.slides') as FormArray;
    }

    isCarouselOptionDisabled(type: CarouselCtaType) {
        return this.welcomePageForm.get('cta.slides').value?.some((j) => j.type === type);
    }

    submit() {
        const payload = this.welcomePageForm.value;

        //Filters disabled or unset slides && Formats back to an array of strings
        payload.cta.slides = payload.cta.slides.filter((i) => i.activated && i.type).map((i) => i.type);

        this.submitForm.emit(payload as Partial<WelcomePageConfig>);
    }

    getCarouselSlideName(type: CarouselCtaType) {
        switch (type) {
            case CarouselCtaType.CocorikoConsultation:
                return 'Consultations Cocoriko';
            case CarouselCtaType.Consultation:
                return 'Consultations';
            case CarouselCtaType.Parking:
                return 'Street parking';
            case CarouselCtaType.CollectionCalendar:
                return 'Collection calendar';
            case CarouselCtaType.ZenCityConsultation:
                return 'Consultations ZenCity';
            case CarouselCtaType.AccessE11Requete:
                return 'Request AccessE11';
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes?.welcomePage?.currentValue) {
            this.welcomePageForm.patchValue(this.buildFormData());
            this.setSlideSelectState();
        }

        if (changes?.loading?.currentValue) {
            this.welcomePageForm.disable();
        }

        if (!changes?.loading?.currentValue) {
            this.welcomePageForm.reset(this.buildFormData());

            this.welcomePageForm.enable();
            this.setSlideSelectState();
        }
    }

    /**
     * Enables or disables type select based on checkbox value
     * @param value
     * @param index
     */
    onSlideCheckboxChange(event: any, index: number) {
        if (event.checked) {
            this.slidesControl.controls[index].get('type').enable();
        } else {
            this.slidesControl.controls[index].get('type').disable();
            this.slidesControl.controls[index].get('type').setValue(0);
        }
    }

    /**
     * Sets the state of the slide type select based on the checkbox value
     */
    private setSlideSelectState() {
        this.slidesControl.controls.forEach((element) => {
            if (element.value.activated) {
                element.get('type').enable();
            } else {
                element.get('type').disable();
            }
        });
    }

    /**
     * Formats the data for the form
     * @returns
     */
    private buildFormData() {
        const formSlidesData = this.maxSlides.map((i, idx) => {
            return {
                activated: this.welcomePage?.cta?.slides[idx] ? true : false,
                type: this.welcomePage?.cta?.slides[idx] ?? 0,
            };
        });

        return {
            cta: {
                interval: this.welcomePage?.cta?.interval ?? 2,
                slides: formSlidesData,
            },
        };
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
